import {
  Box,
  HStack,
  Icon,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
  ThemeIconKey,
} from 'platform/foundation';
import {match} from 'ts-pattern';

// Requirement chips that make a task's "Mandatory" / "File required" flags stand out from the
// surrounding tertiary metadata: a subtle coloured pill with a leading icon. Mandatory reads as a
// warning (amber), file-required as informational (blue).
type TaskRequirementVariant = 'mandatory' | 'fileRequired';

interface TaskRequirementChipProps {
  variant: TaskRequirementVariant;
  label: string;
}

const CHIP_ICON_SIZE = 3;

interface ChipStyle {
  icon: ThemeIconKey;
  backgroundColor: ThemeColorPath;
  iconColor: ThemeColorPath;
  textColor: ThemeColorTextPath;
}

const getChipStyle = (variant: TaskRequirementVariant): ChipStyle =>
  match<TaskRequirementVariant, ChipStyle>(variant)
    .with('mandatory', () => ({
      icon: 'content/asterisk',
      backgroundColor: 'palettes.orange.20.100',
      iconColor: 'palettes.orange.70.100',
      textColor: 'warning',
    }))
    .with('fileRequired', () => ({
      icon: 'editor/insert_drive_file',
      backgroundColor: 'palettes.blue.20.100',
      iconColor: 'palettes.blue.60.100',
      textColor: 'link',
    }))
    .exhaustive();

export function TaskRequirementChip(props: TaskRequirementChipProps) {
  const style = getChipStyle(props.variant);

  return (
    <Box
      paddingHorizontal={2}
      paddingVertical={1}
      borderRadius="small"
      backgroundColor={style.backgroundColor}
    >
      <HStack spacing={1} align="center">
        <Icon value={style.icon} size={CHIP_ICON_SIZE} color={style.iconColor} />
        <Text size="xSmall" alternative color={style.textColor}>
          {props.label}
        </Text>
      </HStack>
    </Box>
  );
}
